import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/useAuth';
import ThemeToggle from '../components/ThemeToggle';
import { hasAdminOrigin } from '../lib/navOrigin';

type NavSection = 'dashboard' | 'admin';

const itemClass = (active: boolean) =>
  `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
    active ? 'bg-primary text-primary-content' : 'hover:bg-base-200'
  }`;

export default function AppLayout() {
  const { currentUser, isAdmin } = useAuth();
  const { pathname, search } = useLocation();

  // A /projects/** page belongs to whichever section the user came from, so the
  // path alone is not enough — without the origin marker an admin drilling into
  // a project from the Admin page would see "Dashboard" highlighted.
  // `isAdmin` is required as well: for a non-admin there is no Admin nav item to
  // highlight, so trusting the URL alone would leave nothing selected at all.
  const section: NavSection =
    pathname.startsWith('/admin') || (isAdmin && hasAdminOrigin(search))
      ? 'admin'
      : 'dashboard';

  const navItems: { to: string; label: string; key: NavSection }[] = [
    { to: '/dashboard', label: 'Dashboard', key: 'dashboard' },
    ...(isAdmin
      ? [{ to: '/admin', label: 'Admin', key: 'admin' as NavSection }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-base-200">
      {/* Top bar */}
      <header className="navbar sticky top-0 z-30 bg-neutral px-2 text-neutral-content shadow sm:px-4">
        <div className="flex flex-1 items-center gap-1">
          {/* Mobile nav (hamburger) — hidden on md+ where the sidebar shows */}
          <div className="dropdown md:hidden">
            <div
              tabIndex={0}
              role="button"
              className="btn btn-ghost btn-sm px-2"
              aria-label="Menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </div>
            <ul
              tabIndex={0}
              className="menu dropdown-content z-30 mt-2 w-52 rounded-box bg-base-100 p-2 text-base-content shadow"
            >
              {navItems.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    className={section === item.key ? 'active' : ''}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <Link to="/dashboard" className="text-sm font-bold sm:text-base">
            PI ULE Checklist &amp; Management Portal
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {currentUser && (
            <div className="hidden text-right sm:block">
              <div className="text-sm font-medium leading-tight">
                {currentUser.name}
              </div>
              <div className="text-xs text-neutral-content/70">
                {currentUser.isAdmin ? 'Admin' : currentUser.access} · @
                {currentUser.alias}
              </div>
            </div>
          )}
          <div className="dropdown dropdown-end">
            <div
              tabIndex={0}
              role="button"
              className="avatar placeholder"
              aria-label={`Account menu for ${currentUser?.name ?? 'user'}`}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-content">
                <span className="text-sm font-semibold leading-none">
                  {currentUser?.name.slice(0, 1).toUpperCase() ?? '?'}
                </span>
              </div>
            </div>
            <ul
              tabIndex={0}
              className="menu dropdown-content z-30 mt-2 w-48 rounded-box bg-base-100 p-2 text-base-content shadow"
            >
              <li className="menu-title text-xs">@{currentUser?.alias}</li>
            </ul>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 p-4 lg:p-6">
        {/* Sidebar */}
        <aside className="hidden w-52 shrink-0 md:block">
          <nav className="sticky top-20 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={itemClass(section === item.key)}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
